/**
 * AttackUI — map-first attack phase. Overlays (player legend, attack-cap
 * + end-phase buttons) sit ABOVE the map. The per-engagement controls
 * and the "last attack" result render BELOW the map as full-width
 * inline panels — they no longer float over territories.
 *
 * Flow:
 *   1. Source candidates (own territory ≥2 armies + enemy neighbor) are
 *      highlighted by default. Tap one → selected; adjacent enemies
 *      highlight; dice defaults to max.
 *   2. Tap a highlighted enemy → action panel appears below the map with
 *      the dice picker + blitz toggle + Attack button.
 *   3. Confirm → server resolves. Source is sticky (kept selected) so
 *      the player can chain attacks; target clears; the result panel
 *      replaces the action panel below the map until the next target.
 *   4. Tap the source again, or the "Cancel" button, or "End Attack", to
 *      deselect / advance.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  FlatList,
} from 'react-native'
import { MapView } from '../map/MapView'
import { useMapDimensions } from '../../lib/useMapDimensions'
import {
  blitzFloorOptions,
  clampBlitzFloor,
  DEFAULT_BLITZ_FLOOR,
  maxDiceForArmies,
  diceCountFromConflict,
  attackErrorMessage,
  resolveBlitz,
  resolveDiceCount,
} from '../../lib/attackDice'
import { renderDice } from '../../lib/gameLog'
import { AttackLogCard } from './AttackLogCard'
import { ContinuousAttackControl } from './ContinuousAttackControl'
import { FormattedLogText } from '../game/FormattedLogText'
import { playerColorSlot } from '../../lib/playerColor'
import { PLAYER_COLORS } from '@warcarp/ui'
import { borderReach, attackDiceSides, defendDiceSides, DEFAULT_DICE_SIDES } from '@warcarp/api-types'
import { diceChip } from '../../lib/ruleOverlayModel'
import { diceEdge } from '../../lib/diceFacts'
import { overlayPalette } from '../../lib/rulesOverlayTheme'
import { RuleChip } from '../map/rules/RuleChip'
import { useAttack, useAdvancePhase } from '../../hooks/useGames'
import { FreeFortifyPanel } from './FreeFortifyPanel'
import { TurnActionCounter } from './TurnActionCounter'
import { TurnPhaseActionRow } from './TurnPhaseActionRow'
import { FortifyButtonLabel } from './FortifyButtonLabel'
import { TurnControlCard } from './TurnControlCard'
import { GAME_CONTROL_EDGE_INSET, FORTIFY_BUTTON_PADDING } from '../../lib/gameChrome'
import { TEAMMATE_WARNING_COLORS, TURN_CARD_SPACING } from '../../lib/turnPanelAppearance'
import { autoAttackTargetId, isTeammateTerritory } from '../../lib/teamPlay'
import { turnMapSelection } from '../../lib/turnMapSelection'
import { shouldResetAttackLog } from '../../lib/attackLog'
import { useAttackLogExpanded } from '../../hooks/useAttackLogExpanded'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import {
  useGameMapSceneRegistration,
  type GameMapSceneRegistration,
} from '../map/gameMapScene'
import { useToast } from '../ui/Toast'
import { useTheme } from '../../hooks/useTheme'
import { AppModal } from '../ui/AppModal'
import type { GameState, Territory } from '../../lib/gameState'
import type { AttackResponse, GamePlayerResponse, GameEvent } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'

interface AttackUIProps {
  gameId: string
  state: GameState
  myPlayerId: string
  slotByPlayerId: Record<string, number>
  playerDisplayName: (playerId: string) => string
  gamePlayers: GamePlayerResponse[]
  viewerUserId?: string
  /** Forwarded to MapView so the player-roster strip pins to the map's
   * right edge and centres on its midpoint (not the screen's). */
  mapRightOverlay?: React.ReactNode
  /** Forwarded to MapView — corner-anchored floating chip (regions toggle). */
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional header rendered at the TOP of the landscape action panel
   * (above the HUD). The game screen colocates the cog + horizontal
   * roster strip here so neither floats over the map.
   */
  actionPanelHeader?: React.ReactNode
  /** Wide layout with persistent chat/log: size the panel to its content
   *  (scrolling past its cap) instead of filling the column. */
  fitContent?: boolean
  /** Thin control bar rendered directly beneath the map (zoom + view mode). */
  mapControlsBar?: React.ReactNode
  /** Game event log — forwarded to MapView for conquest flash animation. */
  events?: GameEvent[]
  /** Forwarded to MapView — zoom-and-pan state managed by the game screen. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (offset: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Forwarded to MapView for bot-attack animation fix (see useAttackEvents). */
  viewerJoinedAt?: number
  /** Time-machine hook — scrubs the game screen into the snapshot view at the given event index. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  /** When set, the parent owns the single shared MapView. */
  mapSceneKey?: string
  onMapSceneConfigChange?: GameMapSceneRegistration
}

export function AttackUI({
  gameId,
  state,
  myPlayerId,
  slotByPlayerId,
  playerDisplayName,
  gamePlayers,
  viewerUserId,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  actionPanelHeader,
  fitContent = false,
  mapControlsBar,
  events,
  zoomEnabled,
  panOffset,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  viewerJoinedAt,
  mapSceneKey,
  onMapSceneConfigChange,
}: AttackUIProps) {
  const attack = useAttack(gameId)
  const advance = useAdvancePhase(gameId)
  const { showToast } = useToast()
  const { theme } = useTheme()
  // Resolve a player's board-colour hex for inline name highlighting in the
  // attack-log summary, mirroring the event-log convention (highlightsForRow).
  // Falls back to grey when the player can't be resolved (neutral / orphan).
  const colorForPlayer = (playerId: string): string => {
    const slot = slotByPlayerId[playerId]
    const gp = slot != null ? gamePlayers.find((p) => p.slot === slot) : undefined
    return gp ? PLAYER_COLORS[playerColorSlot(gp)]?.hex ?? '#888' : '#888'
  }

  const viewerSlot = slotByPlayerId[myPlayerId] ?? 1
  const currentSlot = parseInt(state.players[state.turnIndex] ?? '0', 10)

  const [selectedFromId, setSelectedFromId] = useState<string | null>(null)
  const [selectedToId, setSelectedToId] = useState<string | null>(null)
  const [diceCount, setDiceCount] = useState(1)
  const attackInFlight = useRef(false)
  const [lastResult, setLastResult] = useState<AttackResponse | null>(null)
  // Running log of every attack roll the player has made from the
  // currently-selected source. Cleared when a NEW source is picked — not when
  // the selection merely drops to null (see shouldResetAttackLog). Each entry
  // carries the `defenderId` (owner of the attacked territory at launch time)
  // so the summary can name the defender even after the target is conquered.
  const [attackHistory, setAttackHistory] = useState<(AttackResponse & { defenderId: string })[]>(
    [],
  )
  const [blitz, setBlitz] = useState(false)
  const [blitzFloor, setBlitzFloor] = useState(DEFAULT_BLITZ_FLOOR)
  const [blitzFloorPickerOpen, setBlitzFloorPickerOpen] = useState(false)

  // Reset the log only when a source is actually being PICKED — never when the
  // selection drops to null. A conquest (or attacking until the source runs
  // dry) auto-drops the selection because the board changed, not because the
  // player asked for a different engagement, and clearing there erased the
  // rolls at the exact moment they became worth reading. See lib/attackLog.
  const prevLogSourceRef = useRef<string | null>(null)
  useEffect(() => {
    const previous = prevLogSourceRef.current
    prevLogSourceRef.current = selectedFromId
    if (shouldResetAttackLog(previous, selectedFromId)) {
      setAttackHistory([])
      setLastResult(null)
    }
  }, [selectedFromId])

  const attackCap = state.config.maxAttacksPerTurn
  const capReached = attackCap !== null && state.attacksThisTurn >= attackCap

  const sourceCandidates = useMemo(
    () =>
      state.map.territories.filter((t: Territory) => {
        if (t.ownerId !== myPlayerId) return false
        if (t.armies < 2) return false
        // borderReach = two-way borders + any one-way attack edges, the same
        // rule the server enforces in turn.ts.
        return borderReach(t).some((adjId) => {
          const adj = state.map.territories.find((a) => a.id === adjId)
          return adj && adj.ownerId !== myPlayerId
        })
      }),
    [state, myPlayerId],
  )

  const selectedFrom = selectedFromId
    ? state.map.territories.find((t) => t.id === selectedFromId) ?? null
    : null
  const selectedTo = selectedToId
    ? state.map.territories.find((t) => t.id === selectedToId) ?? null
    : null

  const validTargetIds = useMemo(() => {
    if (!selectedFrom) return []
    return borderReach(selectedFrom).filter((adjId) => {
      const adj = state.map.territories.find((t) => t.id === adjId)
      return adj && adj.ownerId !== myPlayerId
    })
  }, [selectedFrom, state, myPlayerId])

  const maxDice = selectedFrom ? maxDiceForArmies(selectedFrom.armies) : 1
  const blitzFloorChoices = selectedFrom ? blitzFloorOptions(selectedFrom.armies) : []
  const blitzAvailable = blitzFloorChoices.length > 0

  // Dice SIZES for this specific engagement. Most borders are the plain d6-vs-d6
  // both players expect, so an engagement that is anything else has to announce
  // itself — otherwise a d9 tower shelling a d5 flag looks like an ordinary
  // attack until the numbers come up wrong.
  const atkSides = selectedFrom && selectedTo ? attackDiceSides(selectedFrom, selectedTo.id) : DEFAULT_DICE_SIDES
  const defSides = selectedFrom && selectedTo ? defendDiceSides(selectedTo, selectedFrom.id) : DEFAULT_DICE_SIDES
  const oddsAreUnusual = atkSides !== DEFAULT_DICE_SIDES || defSides !== DEFAULT_DICE_SIDES
  // Team games: attacking a teammate is legal, but the popup warns first
  // (plan §12 decision 1). Always false in free-for-all.
  const targetIsTeammate = isTeammateTerritory(state, myPlayerId, selectedTo)
  const warningColor = TEAMMATE_WARNING_COLORS[theme].fill
  const edge = diceEdge(atkSides, defSides)
  const diceEdgeWords = edge === 'even' ? 'even' : edge === 'attacker' ? 'favours you' : 'favours them'

  // Drop the source if it stops qualifying (armies too low after a heavy roll).
  useEffect(() => {
    if (!selectedFromId) return
    const stillValid = sourceCandidates.some((s) => s.id === selectedFromId)
    if (!stillValid) {
      setSelectedFromId(null)
      setSelectedToId(null)
    }
  }, [selectedFromId, sourceCandidates])

  // Post-conquest transfer ("free fortify") — visible while the server
  // has a pending lastConquest pair. Source/target come from server state;
  // the player picks how many additional armies to move from the conquering
  // source into the new territory. Delegated to FreeFortifyPanel.
  //
  // FreeFortifyPanel returns null when maxTransfer === 0 (no armies to
  // move); we use pendingConquest + a non-zero source army count for the
  // visibility guard below so the cap row / Fortify button are suppressed
  // only when the panel is actually showing something.
  const pendingConquest = state.lastConquest ?? null
  const transferSource = pendingConquest
    ? state.map.territories.find((t) => t.id === pendingConquest.fromTerritoryId) ?? null
    : null
  const maxTransfer = transferSource ? Math.max(0, transferSource.armies - 1) : 0
  // freeFortifyVisible drives suppression of the cap row / help text /
  // engagement panel — mirrors the condition inside FreeFortifyPanel so
  // both are in sync without a callback.
  const freeFortifyVisible =
    pendingConquest !== null &&
    transferSource !== null &&
    maxTransfer >= 1

  // Retire the old engagement while the conquest pair owns the map. Waiting
  // for onTransferComplete is too late: the mutation updates the game cache
  // before its promise resolves, briefly exposing the old source's adjacent
  // targets when lastConquest clears. The attack log survives a null source.
  useEffect(() => {
    if (!pendingConquest) return
    setSelectedFromId(null)
    setSelectedToId(null)
  }, [pendingConquest])

  // Keep diceCount in sync WITHOUT clobbering the player's explicit pick. Keyed
  // on the source ID (a stable string) + maxDice (a number) — NOT the
  // selectedFrom object, whose identity churns on every WS state patch and used
  // to reset the pick to max mid-selection. A new source defaults to max; the
  // same source keeps the pick, clamped down only if it weakened. See
  // lib/attackDice.resolveDiceCount.
  const prevSourceIdRef = useRef<string | null>(null)
  useEffect(() => {
    const sourceChanged = prevSourceIdRef.current !== selectedFromId
    prevSourceIdRef.current = selectedFromId
    if (!selectedFromId) return
    setDiceCount((prev) => resolveDiceCount({ prev, sourceChanged, maxDice }))
  }, [selectedFromId, maxDice])

  // Continuous attack is sticky, which is right until the source changes: it used
  // to stay on when you picked a new front with four armies on it, and the next
  // tap spent the lot. Keyed on the source ID + its army count so it also fires
  // when the CURRENT source weakens under the floor after a roll. An explicit
  // toggle on an unchanged source is respected — see lib/attackDice.resolveBlitz.
  const sourceArmies = selectedFrom?.armies ?? null
  // The id AND the army count, because "the source changed" has to include the
  // current source getting thinner — that is the case where a blitz was already
  // running and the next tap would spend what is left.
  const blitzSourceKey = `${selectedFromId ?? ''}:${sourceArmies ?? ''}`
  const prevBlitzSourceRef = useRef<string | null>(null)
  useEffect(() => {
    const sourceChanged = prevBlitzSourceRef.current !== blitzSourceKey
    prevBlitzSourceRef.current = blitzSourceKey
    setBlitz((prev) => resolveBlitz({ prev, sourceChanged, armies: sourceArmies }))
  }, [blitzSourceKey, sourceArmies])

  // Keep the selected stop point legal when a roll weakens the source. The
  // picker is deliberately conservative: its upper bound is source armies - 3
  // so the default three-dice roll can never make the request invalid.
  useEffect(() => {
    if (sourceArmies === null) return
    setBlitzFloor((prev) => clampBlitzFloor(prev, sourceArmies))
    if (!blitzAvailable) setBlitzFloorPickerOpen(false)
  }, [selectedFromId, sourceArmies, blitzAvailable])

  const handleTerritoryPress = useCallback((territoryId: string) => {
    // Free fortify must be resolved (move armies or skip) before the map is
    // interactive again — ignore territory taps until then.
    if (freeFortifyVisible) return
    const territory = state.map.territories.find((t) => t.id === territoryId)
    if (!territory) return

    // Re-tapping the selected source closes the selection.
    if (territoryId === selectedFromId) {
      setSelectedFromId(null)
      setSelectedToId(null)
      setLastResult(null)
      return
    }

    if (!selectedFromId) {
      if (sourceCandidates.some((s) => s.id === territoryId) && !capReached) {
        setSelectedFromId(territoryId)
        setSelectedToId(null)
        setLastResult(null)
      }
    } else if (validTargetIds.includes(territoryId)) {
      setSelectedToId(territoryId)
      setLastResult(null)
    } else if (territory.ownerId === myPlayerId && sourceCandidates.some((s) => s.id === territoryId)) {
      setSelectedFromId(territoryId)
      setSelectedToId(null)
      setLastResult(null)
    } else {
      // Tap on something not part of the current selection → close.
      setSelectedFromId(null)
      setSelectedToId(null)
    }
  }, [freeFortifyVisible, state.map.territories, selectedFromId, sourceCandidates, capReached, validTargetIds, myPlayerId])

  const handleBackgroundPress = useCallback(() => {
    // The free-fortify response owns the map until it is resolved. Outside of
    // that modal state, tapping the ocean/continent background cancels the
    // current from/to attack just like tapping an unrelated territory.
    if (freeFortifyVisible) return
    setSelectedFromId(null)
    setSelectedToId(null)
    setLastResult(null)
  }, [freeFortifyVisible])

  // Auto-select target when the chosen source has exactly one valid
  // target — saves a tap and immediately opens the attack popup. Skipped
  // when the user has already manually picked a target (preserves the
  // user's choice on re-render).
  //
  // Suspended during the free-fortify window. A conquest clears selectedToId,
  // which re-armed this effect and had it pick a NEW enemy behind the dialog —
  // the target then rendered with the secondary (darkened) fill, so the board
  // showed a greyed-out territory that had nothing to do with the free fortify
  // and, on a map with one-way borders, could be several territories away from
  // it. Nothing may be selected until the conquest is resolved.
  useEffect(() => {
    if (freeFortifyVisible) return
    if (!selectedFromId || selectedToId) return
    // Team games skip teammates, so a tap never lands on an ally by default
    // (lib/teamPlay). Free-for-all: the lone valid target, as before.
    const autoTarget = autoAttackTargetId(state, myPlayerId, validTargetIds)
    if (autoTarget) setSelectedToId(autoTarget)
  }, [freeFortifyVisible, selectedFromId, validTargetIds, selectedToId, state, myPlayerId])

  async function handleAttack() {
    if (!selectedFromId || !selectedToId) return
    // Synchronous re-entry guard. `attack.isPending` only flips on React
    // Query's next notification tick, so two presses inside that tick would
    // both reach the server; the second one carries a dice count the first
    // roll may have just made illegal.
    if (attackInFlight.current) return
    attackInFlight.current = true
    // Capture the defender (target owner) BEFORE the attack resolves — a
    // conquest flips the territory to the attacker, losing this info.
    const defenderId = selectedTo?.ownerId ?? ''
    const untilTroops = blitz ? blitzFloor : undefined
    try {
      const result = await attack.mutateAsync({
        fromTerritoryId: selectedFromId,
        toTerritoryId: selectedToId,
        // Clamp at send time as well as in the sync effect: the max is
        // derived from the freshest state this render saw.
        diceCount: Math.min(diceCount, maxDice),
        ...(untilTroops !== undefined ? { untilTroops } : {}),
      })
      setLastResult(result)
      setAttackHistory((prev) => [...prev, { ...result, defenderId }])
      // Keep an unconquered engagement selected for another roll. A conquest
      // retires the target here and the pending-conquest effect retires the
      // source while the server's pair owns the free-fortify view.
      if (result.conquered) {
        setSelectedToId(null)
      } else if (blitz) {
        // Continuous attack ran the source down to the floor without
        // breaking through. Flip the toggle off so the user's next
        // tap is a single attack — they can re-enable continuous if
        // they want another wave.
        setBlitz(false)
      }
    } catch (error) {
      // attack.isError surfaces in the panel. A dice-count conflict (the
      // source thinned under the pick) also re-clamps the picker to what the
      // server will accept now, so the next tap goes through.
      const serverMax = diceCountFromConflict(error)
      if (serverMax !== null) setDiceCount((prev) => Math.min(prev, serverMax))
    } finally {
      attackInFlight.current = false
    }
  }

  const canLaunch = !!selectedFromId && !!selectedToId && !attack.isPending && !capReached

  // Map highlights: candidates before any selection, then targets — and during
  // the free-fortify window, ONLY the conquest pair. See lib/turnMapSelection.
  const mapSelection = useMemo(
    () =>
      turnMapSelection({
        freeFortify: freeFortifyVisible ? pendingConquest : null,
        selectedFromId,
        selectedToId,
        sourceCandidateIds: sourceCandidates.map((t) => t.id),
        targetIds: validTargetIds,
        capReached,
      }),
    [
      freeFortifyVisible,
      pendingConquest,
      selectedFromId,
      selectedToId,
      sourceCandidates,
      validTargetIds,
      capReached,
    ],
  )

  useGameMapSceneRegistration({
    key: mapSceneKey,
    onChange: onMapSceneConfigChange,
    config: {
      selectedTerritoryId: mapSelection.selectedTerritoryId,
      secondaryTerritoryId: mapSelection.secondaryTerritoryId,
      validTargetIds: mapSelection.validTargetIds,
      previewTargetIds: mapSelection.previewTargetIds,
      continuousAttackEnabled: blitz,
      selectedFillMode: 'lighten',
      onTerritoryPress: handleTerritoryPress,
      onBackgroundPress: handleBackgroundPress,
    },
  })

  // Orientation flip: portrait stacks map → action ScrollView vertically;
  // landscape pins the action ScrollView as a fixed-width sidebar on the
  // LEFT so the map fills the rest of the viewport. useMapDimensions
  // surfaces actionPanelWidth (212 on phone landscape, 320 on desktop)
  // so the sidebar reads comfortably at every screen size.
  const { isLandscape, isDesktop, actionPanelWidth } = useMapDimensions(state)
  const [attackLogExpanded, toggleAttackLogExpanded] = useAttackLogExpanded(viewerUserId)
  const actionPanel = (
    <ScrollView
      className={fitContent ? undefined : 'flex-1'}
      contentContainerStyle={{ padding: GAME_CONTROL_EDGE_INSET, gap: 8, flexGrow: fitContent ? 0 : 1 }}
      style={isLandscape ? { maxWidth: actionPanelWidth, ...(fitContent ? { flexGrow: 0, flexShrink: 1 } : null) } : undefined}
    >
        {/* Top "cap row" — visible whenever no action box (engagement
            popup or free-fortify panel) is open, so the user can always
            see attack count + advance phase unless they're mid-combat
            or mid-free-fortify. Selecting just a source (no target yet)
            does NOT count as an open action box — the engagement popup
            requires both — so a sticky-source state after a conquest
            still surfaces the Fortify button. */}
        {!(selectedFromId && selectedToId) && !freeFortifyVisible && (
          <TurnPhaseActionRow counter={<TurnActionCounter action="attacks" used={state.attacksThisTurn} limit={attackCap} compact />}>
            {/* The counter adds breathing room inside the left edge.
                The card aligns with the map bar's outer button edges. */}
            <TouchableOpacity
              onPress={() =>
                advance.mutate(undefined, {
                  // Surface a connection failure instead of letting the button
                  // silently snap back to "Fortify →" (the user wouldn't know
                  // why nothing happened). The fetch now always settles — on a
                  // dead/half-open connection it rejects with a timeout rather
                  // than spinning forever.
                  onError: (err) =>
                    showToast(
                      err instanceof Error ? err.message : 'Could not reach the server — try again',
                      'error',
                    ),
                })
              }
              disabled={advance.isPending}
              accessibilityLabel="Advance to fortify phase"
              accessibilityRole="button"
              className="bg-olive rounded-md py-1.5 items-center justify-center"
              style={{ opacity: advance.isPending ? 0.5 : 1, minHeight: 32, minWidth: 80, paddingHorizontal: FORTIFY_BUTTON_PADDING }}
            >
              {advance.isPending ? (
                <ActivityIndicator size="small" color="#94a3b8" />
              ) : (
                <FortifyButtonLabel />
              )}
            </TouchableOpacity>
          </TurnPhaseActionRow>
        )}
        {/* Post-conquest transfer ("free fortify"): delegated to
            FreeFortifyPanel. The panel renders null when maxTransfer === 0
            (source has no moveable armies after the conquest), which lets
            the cap row / Fortify button above remain visible in that edge
            case. The onTransferComplete callback resets engagement state
            so the player starts fresh after the window closes. */}
        <FreeFortifyPanel
          gameId={gameId}
          state={state}
          onTransferComplete={() => {
            // Drop the engagement — a sticky source carried over from the
            // conquering attack feels disorienting once the post-conquest
            // dialog has appeared. The attack LOG deliberately survives: it is
            // the record of the attack that just won the territory, and
            // resolving the free fortify is not a request to forget it. It
            // clears when the player picks their next source.
            setSelectedFromId(null)
            setSelectedToId(null)
          }}
        />

        {/* Fix 2: Only one action box at a time. Suppress the engagement
            panel while the free-fortify window is open so both panels
            never render simultaneously. The engagement panel returns
            to its normal sticky-source behaviour once the conquest is
            resolved (pendingConquest clears). */}
        {selectedFrom && selectedTo && !freeFortifyVisible && (
          <TurnControlCard
            testID="turn-control-card"
            className="gap-2"
            accent={targetIsTeammate ? 'warning' : oddsAreUnusual ? 'blood' : undefined}
            accessibilityLabel={`${selectedFrom.name} → ${selectedTo.name} popup${
              targetIsTeammate ? `, ${selectedTo.name} belongs to your teammate ${playerDisplayName(selectedTo.ownerId)}` : ''
            }${
              oddsAreUnusual ? `, d${atkSides} versus d${defSides}, ${diceEdgeWords}` : ''
            }`}
          >
            {targetIsTeammate && (
              // The target is an ally's territory: a full-width warning above
              // everything else in the popup (maintainer, 2026-11-03).
              <View testID="attack-teammate-chip" className="flex-row items-center gap-2 rounded-lg px-2.5 py-1.5" style={{ backgroundColor: warningColor }}>
                <MaterialCommunityIcons name="alert" size={18} color="#ffffff" />
                <Text className="text-white font-stencil text-[12px] uppercase flex-1" numberOfLines={2}>
                  Teammate · {playerDisplayName(selectedTo.ownerId)}
                </Text>
              </View>
            )}
            <View className="flex-row items-center justify-between gap-2">
              <Text className="text-ink text-xs font-stencil flex-1 uppercase" numberOfLines={1}>
                {selectedFrom.name} → {selectedTo.name}
              </Text>
              {oddsAreUnusual && (
                // On the title row rather than its own band: this is one short
                // fact and the popup is competing for vertical space. The same
                // "ATK d5 │ DEF d7" chip as the ★ legend (design X6).
                <View
                  testID="attack-dice-chip"
                  accessible
                  accessibilityLabel={`You roll ${atkSides}-sided dice, they defend with ${defSides}-sided dice`}
                >
                  <RuleChip chip={diceChip({ attack: atkSides, defend: defSides }, overlayPalette(theme))} palette={overlayPalette(theme)} compact />
                </View>
              )}
              <TouchableOpacity
                onPress={() => {
                  // Clear BOTH selections — clearing only target re-fires
                  // the auto-select-target effect when validTargetIds has
                  // exactly one entry, which would re-open the popup
                  // immediately (the visible flicker). User-initiated
                  // close means the user wants out of the engagement.
                  setSelectedFromId(null)
                  setSelectedToId(null)
                  setLastResult(null)
                  setAttackHistory([])
                }}
                accessibilityLabel="Close popup"
                accessibilityRole="button"
                className="px-1.5 py-0.5"
              >
                <Text className="text-ink-soft text-base">×</Text>
              </TouchableOpacity>
            </View>

            <View className="flex-row gap-1">
              {[1, 2, 3].map((n) => {
                const enabled = n <= maxDice
                const isSelected = diceCount === n
                return (
                  <TouchableOpacity
                    key={n}
                    onPress={() => enabled && setDiceCount(n)}
                    disabled={!enabled}
                    accessibilityLabel={`${n} dice${!enabled ? ' (unavailable)' : ''}`}
                    accessibilityRole="button"
                    className={`flex-1 py-1 rounded-md items-center border ${
                      isSelected ? 'bg-blood/70 border-blood' : 'bg-paper border-olive/60'
                    }`}
                    style={{ opacity: enabled ? 1 : 0.3, minHeight: 26 }}
                  >
                    <Text className={`font-stencil text-xs ${isSelected ? 'text-white' : 'text-ink-soft'}`}>
                      {n}d
                    </Text>
                  </TouchableOpacity>
                )
              })}
            </View>

            <ContinuousAttackControl value={blitz} available={blitzAvailable}
              onChange={(value) => setBlitz(value && blitzAvailable)}>
              {blitz && (
                <Text className="text-ink-soft text-[10px] font-typed">down to</Text>
              )}
              {blitz && (
                <TouchableOpacity
                  onPress={() => setBlitzFloorPickerOpen(true)}
                  accessibilityLabel={`Continuous attack floor: ${blitzFloor} troops`}
                  accessibilityRole="button"
                  testID="continuous-attack-floor"
                  className="flex-row items-center gap-1 bg-paper-up border border-olive rounded px-2 py-1"
                >
                  <Text className="text-ink text-[10px] font-typed">{blitzFloor} troops</Text>
                  <Text className="text-ink-soft text-[10px]">▾</Text>
                </TouchableOpacity>
              )}
            </ContinuousAttackControl>

            <TouchableOpacity
              onPress={handleAttack}
              disabled={!canLaunch}
              accessibilityLabel={targetIsTeammate
                ? (blitz ? 'Continuous attack on your teammate' : 'Attack teammate')
                : blitz ? 'Continuous attack' : 'Launch attack'}
              accessibilityRole="button"
              testID={targetIsTeammate ? 'attack-teammate-launch' : undefined}
              className={`${targetIsTeammate ? '' : 'bg-blood '}rounded-xl py-2.5 items-center flex-row gap-1 justify-center`}
              style={{ opacity: canLaunch ? 1 : 0.4, minHeight: 44, marginTop: TURN_CARD_SPACING.placementActionsTop,
                ...(targetIsTeammate ? { backgroundColor: warningColor } : null) }}
            >
              {attack.isPending && <ActivityIndicator size="small" color="white" />}
              <Text className="text-white font-stencil text-sm uppercase">
                {/* Just the verb (maintainer, 2026-11-03): the dice count read
                    as noise, and the dice chip on the title row carries the
                    die sizes. */}
                {targetIsTeammate
                  ? (blitz ? `Attack teammate down to ${blitzFloor}` : 'Attack teammate')
                  : blitz
                  ? `Attack down to ${blitzFloor}`
                  : 'Attack'}
              </Text>
            </TouchableOpacity>

            {attack.isError && (
              <Text className="text-blood text-[10px] font-typed">
                {attackErrorMessage(attack.error)}
              </Text>
            )}
          </TurnControlCard>
        )}

        {/* Attack log — a single running list of EVERY roll made from the
            currently-selected source (across single + continuous attacks),
            flattened and numbered 1..N in the order they happened. It grows
            as more attacks come in and clears when the source changes. The
            title shows the total roll count; the line under it aggregates
            total losses. Each row uses the event-log
            "dice vs dice (x lost, y lost)" format with enlarged dice. */}
        {(attackHistory.length > 0 || attack.isPending) && (() => {
          // Flatten every attack's rolls into one chronological list, and
          // aggregate the server-authoritative loss totals + conquest flag.
          const flat = attackHistory.flatMap((r) =>
            r.rolls && r.rolls.length > 0 ? r.rolls : [r.roll],
          )
          const atkLost = attackHistory.reduce((s, r) => s + r.attackerLost, 0)
          const defLost = attackHistory.reduce((s, r) => s + r.defenderLost, 0)
          const conquered = attackHistory.some((r) => r.conquered)
          // Summary names: the viewer is the attacker; the defender is the most
          // recent target's owner (captured at launch). '' owner → neutral.
          const attackerName = playerDisplayName(myPlayerId)
          const defenderId = attackHistory[attackHistory.length - 1]?.defenderId ?? ''
          const defenderName = defenderId ? playerDisplayName(defenderId) : 'neutral'
          const summary = `${attackerName} (${atkLost} lost) vs ${defenderName} (${defLost} lost)`
          const summaryHighlights = [
            { text: attackerName, color: colorForPlayer(myPlayerId) },
            ...(defenderId ? [{ text: defenderName, color: colorForPlayer(defenderId) }] : []),
          ]
          return (
            <AttackLogCard rollCount={flat.length} summary={summary} highlights={summaryHighlights}
              conquered={conquered} expanded={attackLogExpanded}
              onToggle={toggleAttackLogExpanded}>
              {attackLogExpanded && flat.map((rr, i) => {
                const aMin = Math.min(rr.attacker.length, rr.defender.length)
                let aLost = 0
                let dLost = 0
                for (let k = 0; k < aMin; k++) {
                  if (rr.attacker[k]! > rr.defender[k]!) dLost++
                  else aLost++
                }
                return (
                  <FormattedLogText key={i} className="text-ink text-sm font-typed"
                    text={`${i + 1}. ${renderDice(rr.attacker)} vs ${renderDice(rr.defender)} (${aLost} lost, ${dLost} lost)`}
                    diceSize={26}
                    fontSize={14}
                    color={theme === 'dark' ? '#e6e1c4' : '#18170f'}
                  />
                )
              })}
              {/* Rolling row — acknowledges the tap where the ANSWER will appear.
                  The dice come from the server, so the result cannot be predicted;
                  what can be fixed is the dead air. Measured: ~148ms round trip to
                  production plus ~62ms p95 server time, against 6ms for the client
                  to paint the response (docs/map-perf.md). Without this the log
                  keeps showing the PREVIOUS roll while the new one resolves, so a
                  tap looks ignored for a fifth of a second. */}
              {/* The rolling row is part of the expanded list only — the
                  collapsed preview stays the same size. */}
              {attackLogExpanded && attack.isPending && (
                <View className="flex-row items-center gap-2">
                  <Text className="text-ink-soft text-sm font-typed">
                    {flat.length + 1}. rolling
                  </Text>
                  <ActivityIndicator size="small" color="#8a8c6e" />
                </View>
              )}
            </AttackLogCard>
          )
        })()}
      </ScrollView>
  )

  // Keep the picker outside the action ScrollView. Besides matching the modal
  // portal's visual ownership, this prevents the vertically-scrolling picker
  // from being treated as a nested list by React Native.
  const blitzFloorModal = (
    <AppModal
      visible={blitzFloorPickerOpen}
      transparent
      animationType="fade"
      onRequestClose={() => setBlitzFloorPickerOpen(false)}
    >
      <View className="flex-1 justify-center bg-black/50 px-5">
        <View className="bg-canvas rounded-xl border-2 border-olive p-4 gap-3">
          <View>
            <Text className="text-ink font-stencil text-base uppercase">
              Continuous attack
            </Text>
            <Text className="text-ink-soft font-typed text-xs mt-1">
              Swipe to browse, then tap where to stop.
            </Text>
          </View>
          <FlatList
            data={blitzFloorChoices}
            keyExtractor={(floor) => String(floor)}
            initialScrollIndex={Math.max(0, blitzFloorChoices.indexOf(blitzFloor))}
            getItemLayout={(_, index) => ({ length: 48, offset: 48 * index, index })}
            showsVerticalScrollIndicator
            style={{ maxHeight: 240 }}
            renderItem={({ item: floor }) => {
              const selected = floor === blitzFloor
              return (
                <TouchableOpacity
                  onPress={() => {
                    setBlitzFloor(floor)
                    setBlitzFloorPickerOpen(false)
                  }}
                  accessibilityLabel={`Stop at ${floor} troops`}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  className={`items-center justify-center rounded-md border-2 px-3 ${
                    selected ? 'bg-blood border-ink' : 'bg-paper-up border-olive'
                  }`}
                  style={{ minHeight: 44, marginVertical: 2 }}
                >
                  <Text className={`font-stencil text-xs ${selected ? 'text-paper' : 'text-ink'}`}>
                    {floor} troops
                  </Text>
                </TouchableOpacity>
              )
            }}
          />
          <TouchableOpacity
            onPress={() => setBlitzFloorPickerOpen(false)}
            accessibilityLabel="Cancel continuous attack floor selection"
            accessibilityRole="button"
            className="items-center rounded-md border border-olive py-2"
          >
            <Text className="font-stencil text-xs text-ink-soft uppercase">Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </AppModal>
  )

  // Centring wrapper only applies in LANDSCAPE. In portrait the action
  // panel below the map is `flex-1` (a ScrollView) — wrapping the map
  // in a flex-1 sibling would split the viewport 50/50 between the two
  // and push the action panel off-screen (a portrait-mobile regression).
  // In landscape the wrapper centres the map within its horizontal
  // budget, which is useful on desktop where containerWidth = svgWidth
  // leaves spare horizontal space; on phone landscape it's a no-op
  // since the map fills its container width.
  const mapNode = (
    <MapView
      state={state}
      viewerSlot={viewerSlot}
      selectedTerritoryId={mapSelection.selectedTerritoryId}
      secondaryTerritoryId={mapSelection.secondaryTerritoryId}
      validTargetIds={mapSelection.validTargetIds}
      previewTargetIds={mapSelection.previewTargetIds}
      onTerritoryPress={handleTerritoryPress}
      onBackgroundPress={handleBackgroundPress}
      selectedFillMode="lighten"
      rightOverlay={mapRightOverlay}
      regionToggleNode={regionToggleNode}
      regionToggleCorner={regionToggleCorner}
      zoomToggleNode={zoomToggleNode}
      zoomToggleCorner={zoomToggleCorner}
      events={events}
      hideOnMapZoomButtons={!!mapControlsBar}
      reservedBottom={isLandscape && mapControlsBar ? 44 : undefined}
      zoomEnabled={zoomEnabled}
      panOffset={panOffset}
      onPanChange={onPanChange}
      zoomScale={zoomScale}
      onZoomScaleChange={onZoomScaleChange}
      viewerJoinedAt={viewerJoinedAt}
    />
  )
  if (mapSceneKey && onMapSceneConfigChange) {
    return isLandscape ? (
      <>
        <View className="flex-col" style={{ width: actionPanelWidth }}>
          {actionPanelHeader}
          {actionPanel}
        </View>
        {blitzFloorModal}
      </>
    ) : (
      <>
        {actionPanel}
        {blitzFloorModal}
      </>
    )
  }
  return (
    <>
      <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
        {isLandscape && (
          <View className="flex-col" style={{ width: actionPanelWidth }}>
            {actionPanelHeader}
            {actionPanel}
          </View>
        )}
        {isLandscape ? (
          <View className="flex-1 border-l border-olive bg-paper">
            <View className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}>{mapNode}</View>
            <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
          </View>
        ) : (
          mapNode
        )}
        {!isLandscape && mapControlsBar}
        {!isLandscape && actionPanel}
      </View>
      {blitzFloorModal}
    </>
  )
}
